import { useState, useRef } from "react";
import { Volume2, Play, Pause } from "lucide-react";

const PHRASE = "こんにちは。おはようございます。ありがとう。さくら。";

const KANA_TESTS = [
  { slug: "kana_tests/a2_double", label: "あ (double: ああ)", tag: "sounds like long 'a'?" },
  { slug: "kana_tests/a2_long", label: "あ (long: あー)", tag: "sounds like 'aaa' (prolonged)?" },
  { slug: "kana_tests/i2_double", label: "い (double: いい)", tag: "sounds like long 'i'?" },
  { slug: "kana_tests/i2_long", label: "い (long: いー)", tag: "sounds like 'iii' (prolonged)?" },
  { slug: "kana_tests/u2_double_nosep", label: "う (double: うう)", tag: "long 'ou'? no 'tt' artifact?" },
  { slug: "kana_tests/u2_long_mark", label: "う (long: うー)", tag: "'ouuu' prolonged?" },
  { slug: "kana_tests/u2_period_only", label: "う (avec point: う。)", tag: "single u + pause" },
  { slug: "kana_tests/u2_brackets", label: "う (crochets: 「う」)", tag: "single u in JP brackets" },
];

const FEMALE = [
  { slug: "1_yoko_actuelle", label: "Atsuko (ACTUELLE)", tag: "kawaii • standard", warn: true },
  { slug: "F_maiko",        label: "Maiko Kokusaki",       tag: "narratrice calme • KANTO (Tokyo)", best: true },
  { slug: "F_kana",         label: "Kana",                 tag: "jeune • narratrice • standard" },
  { slug: "F_saori",        label: "Saori",                tag: "pédagogique • standard" },
  { slug: "F_suisen",       label: "Suisen",               tag: "conversation • standard" },
  { slug: "F_aya",          label: "Aya",                  tag: "amicale casual • standard" },
  { slug: "F_tsuki",        label: "Tsuki",                tag: "tendre • KANSAI (Osaka)" },
];

const MALE = [
  { slug: "M_1_kenzo_actuelle", label: "Alexis (ACTUELLE)", tag: "jeune pro • standard", warn: true },
  { slug: "M_shin",         label: "Shin",                 tag: "narrateur tech calme • standard", best: true },
  { slug: "M_hiroki",        label: "Hiroki",               tag: "calme précis • standard" },
  { slug: "M_yoshio",        label: "Yoshio",               tag: "落ち着いたナレーター • standard" },
  { slug: "M_koichi",        label: "Koichi Yashiro",       tag: "voix cinématique profonde" },
  { slug: "M_takuya",        label: "Takuya",               tag: "jeune • standard" },
  { slug: "M_otani",         label: "Otani",                tag: "confiance • standard" },
];

function VoiceCard({ v, playing, onPlay }) {
  const isPlaying = playing === v.slug;
  return (
    <div
      className={`p-4 rounded-2xl border-2 transition-all ${
        v.best ? "border-emerald-500 bg-emerald-50" :
        v.warn ? "border-red-300 bg-red-50" :
        "border-[#1A2A40]/10 bg-white hover:border-[#E94A3F]"
      }`}
      data-testid={`voice-card-${v.slug}`}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={() => onPlay(v.slug)}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${
            isPlaying ? "bg-[#1A2A40] text-white" : "bg-[#E94A3F] text-white"
          }`}
          data-testid={`play-${v.slug}`}
          aria-label={`Écouter ${v.label}`}
        >
          {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#1A2A40] flex items-center gap-2 flex-wrap">
            {v.label}
            {v.best && <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full uppercase font-bold">Recommandée</span>}
            {v.warn && <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full uppercase font-bold">Actuelle</span>}
          </div>
          <div className="text-sm text-[#1A2A40]/60">{v.tag}</div>
        </div>
      </div>
    </div>
  );
}

export default function VoiceAudition() {
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const play = (slug) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playing === slug) {
      setPlaying(null);
      return;
    }
    const a = new Audio(`/voice_samples/${slug}.mp3`);
    audioRef.current = a;
    a.onended = () => setPlaying(null);
    a.play().then(() => setPlaying(slug)).catch((e) => {
      console.warn("Play failed:", e);
      setPlaying(null);
    });
  };

  return (
    <div className="washi-bg min-h-screen py-12 px-4" data-testid="voice-audition-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-bold text-[#1A2A40] mb-3">
          Audition des voix japonaises
        </h1>
        <p className="text-lg text-[#1A2A40]/70 mb-3">
          14 voix natives japonaises disponibles chez ElevenLabs. Toutes générées avec le modèle
          <code className="bg-white px-2 py-0.5 rounded mx-1 text-sm">eleven_turbo_v2_5</code>
          + <code className="bg-white px-2 py-0.5 rounded mx-1 text-sm">language_code="ja"</code>.
        </p>
        <p className="text-base text-[#1A2A40]/70 mb-8 italic">
          Phrase test : « {PHRASE} »<br/>
          <span className="text-sm">(konnichiwa. ohayou gozaimasu. arigatou. sakura.)</span>
        </p>

        <div className="mb-8 p-6 rounded-2xl bg-amber-50 border-2 border-amber-400">
          <h2 className="text-2xl font-bold text-[#1A2A40] mb-2 flex items-center gap-2">
            🚨 Test kanas isolés : comparer les stratégies d'expansion
          </h2>
          <p className="text-sm text-[#1A2A40]/70 mb-4">
            Le problème "a atta / i itti / u uttu" venait de la virgule japonaise 、 interprétée comme un ッ.
            Voici 4 stratégies alternatives sans virgule. Dis-moi laquelle sonne le mieux (double = doublé sans séparateur, long = avec marqueur ー de voyelle longue).
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {KANA_TESTS.map((v) => <VoiceCard key={v.slug} v={v} playing={playing} onPlay={play} />)}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A2A40] mb-4 flex items-center gap-2">
            <Volume2 className="w-6 h-6 text-[#E94A3F]" /> Voix féminines (7)
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEMALE.map((v) => <VoiceCard key={v.slug} v={v} playing={playing} onPlay={play} />)}
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-[#1A2A40] mb-4 flex items-center gap-2">
            <Volume2 className="w-6 h-6 text-[#E94A3F]" /> Voix masculines (7)
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {MALE.map((v) => <VoiceCard key={v.slug} v={v} playing={playing} onPlay={play} />)}
          </div>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-[#1A2A40] text-white">
          <h3 className="font-bold text-lg mb-2">Comment choisir ?</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Écoute d'abord les 2 marquées <span className="text-emerald-300 font-bold">RECOMMANDÉE</span> (accent Kanto = Tokyo).</li>
            <li>Compare avec les 2 marquées <span className="text-red-300 font-bold">ACTUELLE</span> (celles qui te semblent chinoises).</li>
            <li>Écoute les autres si tu veux comparer.</li>
            <li>Dis-moi le slug de la femme + de l'homme que tu préfères. Je change instantanément.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
